import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { IS_RECMAPPER } from './product';
import AutoTopupPanel from './components/AutoTopupPanel';

interface EnrichmentJob {
  id: number;
  file_name: string;
  enrichment_type: string;
  total_rows: number;
  processed_rows: number;
  successful_rows: number;
  failed_rows: number;
  credits_used: number;
  status: string;
  uploaded_at: string;
  completed_at: string;
  user_id: number;
  user_name: string;
  user_email: string;
}

interface EnrichmentResult {
  id: number;
  entity_type: string;
  linkedin_url: string;
  enrichment_status: string;
  first_name: string;
  last_name: string;
  headline: string;
  company_name: string;
  error_message: string;
}

interface TeamMember {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  team_role: string;
  credit_limit: number | null;
  credit_limit_enabled: boolean;
  balance: number;
  usage_this_cycle: number;
}

interface ChartDataPoint {
  date: string;
  total: number;
  work_email: number;
  personal_email: number;
  phone: number;
  profile: number;
  other: number;
}

interface CreditBundle {
  id: number;
  credits_purchased: number;
  credits_remaining: number;
  price_paid: number;
  purchased_at: string;
  expires_at: string;
  purchased_by: string;
  source?: string; // 'purchase' | 'tier_inclusion' | 'auto_topup'
  status: 'active' | 'expired' | 'used';
}

interface AutoTopupEvent {
  id: number;
  status: 'paid' | 'failed' | 'paid_unfulfilled' | string;
  credits: number;
  price_pence: number;
  balance_before: number | null;
  threshold: number | null;
  error: string | null;
  hosted_invoice_url: string | null;
  created_at: string;
  billed_to: string;
  triggered_by: string | null;
}

interface CreditTransaction {
  id: number;
  user_id: number;
  transaction_type: string;
  amount: number;
  description: string;
  enrichment_id: string | null;
  created_at: string;
  user_name: string;
  user_email: string;
}

interface FullEnrichBatch {
  enrichment_id: string;
  type: string;
  entity_type: string;
  total_contacts: number;
  found_count: number;
  credits_used: number;
  status: string;
  created_at: string;
  user_id: number;
  user_name: string;
  user_email: string;
}

interface FullEnrichContact {
  id: number;
  first_name: string;
  last_name: string;
  headline: string;
  company: string;
  email: string | null;
  personal_email: string | null;
  phone: string | null;
  linkedin_url: string | null;
  enrichment_status: string;
  credits_used: number;
}

interface MonthlySummary {
  month: string;
  total: number;
  work_email: number;
  personal_email: number;
  phone: number;
  profile: number;
}

const CreditUsage: React.FC = () => {
  const { token, user } = useAuth();
  const [activeSubTab, setActiveSubTab] = useState<'members' | 'bundles' | 'usage'>('members');
  const [jobs, setJobs] = useState<EnrichmentJob[]>([]);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [isTeam, setIsTeam] = useState(false);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState<EnrichmentJob | null>(null);
  const [jobResults, setJobResults] = useState<EnrichmentResult[]>([]);
  const [jobResultsLoading, setJobResultsLoading] = useState(false);
  const [bundles, setBundles] = useState<CreditBundle[]>([]);
  const [monthlySummary, setMonthlySummary] = useState<MonthlySummary[]>([]);
  const [fullenrichBatches, setFullenrichBatches] = useState<FullEnrichBatch[]>([]);
  const [selectedBatch, setSelectedBatch] = useState<FullEnrichBatch | null>(null);
  const [batchContacts, setBatchContacts] = useState<FullEnrichContact[]>([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [limitSaving, setLimitSaving] = useState<number | null>(null);
  const [autoTopups, setAutoTopups] = useState<AutoTopupEvent[]>([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usageRes, membersRes, chartRes, bundlesRes, summaryRes] = await Promise.all([
        fetch('/api/credits/usage', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/credits/team-members', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/credits/usage-chart', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/credits/bundles', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/credits/usage-summary', { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (usageRes.ok) {
        const data = await usageRes.json();
        setJobs(data.enrichment_jobs || data.jobs || []);
        setTransactions(data.transactions || []);
        setFullenrichBatches(data.fullenrich_batches || []);
        setAutoTopups(data.auto_topups || []);
      }
      if (membersRes.ok) {
        const data = await membersRes.json();
        setMembers(data.members || []);
        setIsTeam(data.is_team);
      }
      if (chartRes.ok) {
        const data = await chartRes.json();
        setChartData(data.chart || []);
      }
      if (bundlesRes.ok) {
        const data = await bundlesRes.json();
        setBundles(data.bundles || []);
      }
      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setMonthlySummary(data.summary || []);
      }
    } catch (err) {
      console.error('Failed to fetch credit usage data:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchBatchDetails = async (batch: FullEnrichBatch) => {
    setSelectedBatch(batch);
    setSelectedJob(null);
    setBatchLoading(true);
    try {
      const res = await fetch(`/api/credits/usage/fullenrich/${encodeURIComponent(batch.enrichment_id)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setBatchContacts(data.results || []);
      }
    } catch (err) {
      console.error('Failed to fetch batch details:', err);
    } finally {
      setBatchLoading(false);
    }
  };

  const fetchJobDetails = async (job: EnrichmentJob) => {
    setSelectedJob(job);
    setSelectedBatch(null);
    setJobResultsLoading(true);
    try {
      const res = await fetch(`/api/credits/usage/${job.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setJobResults(data.results || []);
      }
    } catch (err) {
      console.error('Failed to fetch job details:', err);
    } finally {
      setJobResultsLoading(false);
    }
  };

  const updateCreditLimit = async (memberId: number, enabled: boolean, limit: number) => {
    setLimitSaving(memberId);
    try {
      const res = await fetch(`/api/credits/team-members/${memberId}/limit`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ credit_limit_enabled: enabled, credit_limit: limit })
      });
      if (res.ok) {
        setMembers(prev => prev.map(m =>
          m.id === memberId
            ? { ...m, credit_limit_enabled: enabled, credit_limit: enabled ? limit : null }
            : m
        ));
      }
    } catch (err) {
      console.error('Failed to update credit limit:', err);
    } finally {
      setLimitSaving(null);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatTimeAgo = (dateStr: string) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min. ago`;
    if (diffHours < 24) return `${diffHours} hr. ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return formatDate(dateStr);
  };

  // ============================================
  // Usage Chart (stacked CSS bar chart with hover popup)
  // ============================================
  const renderChart = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    // Build lookup by day-of-month
    const dataByDate: Record<string, ChartDataPoint> = {};
    chartData.forEach(d => {
      const dateKey = new Date(d.date).getDate().toString();
      dataByDate[dateKey] = d;
    });

    // Get max total for scaling
    const totals = chartData.map(d => d.total || 0);
    const maxVal = Math.max(1, ...totals);

    // Generate ticks for y-axis
    const yTicks: number[] = [];
    const step = Math.ceil(maxVal / 4 / 100) * 100 || 50;
    for (let i = 0; i <= maxVal + step; i += step) {
      yTicks.push(i);
    }
    const chartMax = yTicks[yTicks.length - 1] || maxVal;

    const today = now.getDate();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Calculate current month total for header
    const thisMonthTotal = chartData.reduce((sum, d) => sum + (d.total || 0), 0);

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-700">Credit Usage This Month</h3>
          <span className="text-sm font-semibold text-purple-600">{thisMonthTotal.toLocaleString()} credits</span>
        </div>

        <div className="relative" style={{ height: '200px' }}>
          {/* Y-axis labels */}
          <div className="absolute left-0 top-0 bottom-6 w-12 flex flex-col justify-between text-xs text-gray-400">
            {[...yTicks].reverse().map(v => (
              <span key={v}>{v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : v}</span>
            ))}
          </div>
          {/* Bars */}
          <div className="ml-14 h-full flex items-end gap-px pb-6" style={{ paddingRight: '4px' }}>
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const dp = dataByDate[day.toString()];
              const val = dp?.total || 0;
              const heightPct = chartMax > 0 ? (val / chartMax) * 100 : 0;
              const isToday = day === today;
              const isFuture = day > today;

              return (
                <div key={day} className="flex-1 flex flex-col items-center justify-end h-full relative group/bar">
                  {isFuture ? (
                    <div className="w-full bg-gray-50 rounded-t" style={{ height: '2px' }} />
                  ) : val > 0 ? (
                    <div
                      className={`w-full rounded-t flex flex-col-reverse overflow-hidden ${isToday ? 'ring-2 ring-purple-500' : ''}`}
                      style={{ height: `${Math.max(heightPct, 3)}%`, minHeight: '4px' }}
                    >
                      {/* Stacked segments: work_email (blue), personal_email (green), phone (yellow), profile (purple), other (gray) */}
                      {dp && val > 0 && (() => {
                        const segments = [
                          { value: dp.work_email || 0, color: 'bg-blue-400' },
                          { value: dp.personal_email || 0, color: 'bg-green-400' },
                          { value: dp.phone || 0, color: 'bg-yellow-400' },
                          { value: dp.profile || 0, color: 'bg-purple-400' },
                          { value: dp.other || 0, color: 'bg-gray-300' },
                        ].filter(s => s.value > 0);

                        // If no breakdown data, show as single purple bar
                        if (segments.length === 0) {
                          return <div className="w-full h-full bg-purple-400" />;
                        }

                        return segments.map((seg, idx) => (
                          <div
                            key={idx}
                            className={`w-full ${seg.color}`}
                            style={{ height: `${(seg.value / val) * 100}%`, minHeight: '1px' }}
                          />
                        ));
                      })()}
                    </div>
                  ) : (
                    <div className={`w-full bg-gray-100 rounded-t ${isToday ? 'ring-2 ring-purple-500' : ''}`} style={{ height: '2px' }} />
                  )}
                  {/* Per-day hover tooltip */}
                  {val > 0 && (
                    <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/bar:opacity-100 transition-opacity whitespace-nowrap z-30">
                      {monthNames[month]} {day}: {val.toLocaleString()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {/* X-axis labels */}
          <div className="ml-14 flex justify-between text-xs text-gray-400 mt-1">
            <span>{monthNames[month]} 1st</span>
            <span>{monthNames[month]} {Math.floor(daysInMonth / 2)}th</span>
            <span>Today</span>
            <span>{monthNames[month]} {daysInMonth}st</span>
          </div>
        </div>
        {/* Legend */}
        <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-blue-400" /> Work Email</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-green-400" /> Personal Email</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-yellow-400" /> Phone</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-purple-400" /> Profiles</span>
        </div>
      </div>
    );
  };

  // ============================================
  // Members Tab
  // ============================================
  const renderMembersTab = () => {
    const isAdmin = user?.teamRole === 'admin' || !isTeam;

    return (
      <div className="bg-white rounded-lg border border-gray-200 overflow-visible">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Usage (this cycle)</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Credit Limit</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {members.map(member => (
              <MemberRow
                key={member.id}
                member={member}
                isAdmin={isAdmin}
                saving={limitSaving === member.id}
                onUpdateLimit={updateCreditLimit}
                monthlySummary={monthlySummary}
              />
            ))}
            {members.length === 0 && (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                  No team members found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  // ============================================
  // Helper: categorise a transaction from its description
  // ============================================
  const categoriseTransaction = (desc: string): { label: string; icon: string; color: string } => {
    const d = (desc || '').toLowerCase();
    if (d.includes('added candidate'))     return { label: 'Candidate Added',            icon: 'user',     color: 'blue' };
    if (d.includes('added hiring manager'))return { label: 'Hiring Manager Added',       icon: 'briefcase',color: 'indigo' };
    if (d.includes('added company'))       return { label: 'Company Added',              icon: 'building', color: 'teal' };
    if (d.includes('enriched candidate'))  return { label: 'Candidate Enriched',         icon: 'sparkle',  color: 'purple' };
    if (d.includes('enriched company'))    return { label: 'Company Enriched',           icon: 'sparkle',  color: 'purple' };
    if (d.includes('work email'))          return { label: 'FullEnrich - Contact Found', icon: 'mail',     color: 'green' };
    if (d.includes('personal email'))      return { label: 'FullEnrich - Contact Found', icon: 'mail',     color: 'green' };
    if (d.includes('phone'))              return { label: 'FullEnrich - Phone Found',   icon: 'phone',    color: 'green' };
    if (d.includes('bulk enrichment'))     return { label: 'Bulk Enrichment',            icon: 'stack',    color: 'orange' };
    return { label: 'Credit Used', icon: 'bolt', color: 'gray' };
  };

  const renderCategoryIcon = (icon: string, color: string): React.ReactElement => {
    const colorClasses: Record<string, string> = {
      blue:   'bg-blue-100 text-blue-600',
      indigo: 'bg-indigo-100 text-indigo-600',
      teal:   'bg-teal-100 text-teal-600',
      purple: 'bg-purple-100 text-purple-600',
      green:  'bg-green-100 text-green-600',
      orange: 'bg-orange-100 text-orange-600',
      gray:   'bg-gray-100 text-gray-500',
    };
    const cls = colorClasses[color] || colorClasses.gray;

    const icons: Record<string, React.ReactElement> = {
      user: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
      briefcase: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      building: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      sparkle: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      ),
      mail: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      phone: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      ),
      stack: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      ),
      bolt: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
    };

    return (
      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${cls}`}>
        {icons[icon] || icons.bolt}
      </div>
    );
  };

  // ============================================
  // Usage Log Tab - shows all individual credit transactions
  // ============================================
  // ============================================
  // FullEnrich Batch Detail View
  // ============================================
  const renderBatchDetail = () => {
    if (!selectedBatch) return null;

    return (
      <div>
        <button
          onClick={() => { setSelectedBatch(null); setBatchContacts([]); }}
          className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to usage log
        </button>

        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Bulk {selectedBatch.entity_type === 'candidate' ? 'Candidate' : 'Manager'} Enrichment - {selectedBatch.total_contacts} contacts
            </h3>
            <p className="text-sm text-gray-500">
              by {selectedBatch.user_name || selectedBatch.user_email} &middot; {formatDate(selectedBatch.created_at)}
            </p>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-gray-600">Sent: <strong>{selectedBatch.total_contacts}</strong></span>
            <span className="text-green-600">Found: <strong>{selectedBatch.found_count}</strong></span>
            <span className="text-purple-600">Credits: <strong>{selectedBatch.credits_used}</strong></span>
          </div>
        </div>

        {batchLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {batchContacts.map(contact => {
                  const hasAnyData = contact.email || contact.personal_email || contact.phone;
                  return (
                    <tr key={contact.id} className="hover:bg-gray-50">
                      <td className="px-6 py-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {contact.first_name} {contact.last_name}
                          </p>
                          {contact.headline && (
                            <p className="text-xs text-gray-500 truncate max-w-xs">{contact.headline}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-600">
                        {contact.company || '-'}
                      </td>
                      <td className="px-6 py-3">
                        <div className="space-y-0.5">
                          {contact.email && (
                            <p className="text-sm text-gray-900 flex items-center gap-1">
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-50 text-blue-700">W</span>
                              {contact.email}
                            </p>
                          )}
                          {contact.personal_email && (
                            <p className="text-sm text-gray-900 flex items-center gap-1">
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-green-50 text-green-700">P</span>
                              {contact.personal_email}
                            </p>
                          )}
                          {!contact.email && !contact.personal_email && (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-600">
                        {contact.phone || '-'}
                      </td>
                      <td className="px-6 py-3 text-right">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          hasAnyData
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {hasAnyData ? (
                            <>
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Found
                            </>
                          ) : (
                            'Not found'
                          )}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {batchContacts.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                      No contacts found for this batch
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderUsageTab = () => {
    if (selectedJob) {
      return renderJobDetail();
    }
    if (selectedBatch) {
      return renderBatchDetail();
    }

    // Merge individual transactions, bulk enrichment jobs, and FullEnrich batches into a unified timeline
    type TimelineItem = {
      key: string;
      date: string;
      type: 'transaction' | 'bulk_job' | 'fullenrich_batch' | 'auto_topup';
      transaction?: CreditTransaction;
      job?: EnrichmentJob;
      batch?: FullEnrichBatch;
      topup?: AutoTopupEvent;
    };

    const timeline: TimelineItem[] = [];

    // Automatic top-ups (paid and failed) — the owner should see every attempt
    autoTopups.forEach(t => {
      timeline.push({
        key: `topup-${t.id}`,
        date: t.created_at,
        type: 'auto_topup',
        topup: t,
      });
    });

    // Add individual transactions (these are non-FullEnrich transactions now)
    transactions.forEach(t => {
      timeline.push({
        key: `tx-${t.id}`,
        date: t.created_at,
        type: 'transaction',
        transaction: t,
      });
    });

    // Add CSV bulk enrichment jobs
    jobs.forEach(j => {
      timeline.push({
        key: `job-${j.id}`,
        date: j.uploaded_at,
        type: 'bulk_job',
        job: j,
      });
    });

    // Add FullEnrich batches
    fullenrichBatches.forEach(b => {
      timeline.push({
        key: `fe-${b.enrichment_id}`,
        date: b.created_at,
        type: 'fullenrich_batch',
        batch: b,
      });
    });

    // Sort by date descending
    timeline.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    // Group by date for display
    const grouped: Record<string, TimelineItem[]> = {};
    timeline.forEach(item => {
      const dateKey = new Date(item.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
      if (!grouped[dateKey]) grouped[dateKey] = [];
      grouped[dateKey].push(item);
    });

    const renderBatchRow = (batch: FullEnrichBatch, key: string) => (
      <div
        key={key}
        onClick={() => fetchBatchDetails(batch)}
        className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
      >
        <div className="flex items-center gap-3">
          {renderCategoryIcon('mail', 'green')}
          <div>
            <p className="text-sm font-medium text-gray-900">
              Bulk {batch.entity_type === 'candidate' ? 'Candidate' : 'Manager'} Enrichment - {batch.total_contacts} contacts
            </p>
            <p className="text-xs text-gray-500">
              by {batch.user_name || batch.user_email}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-500">Sent: <strong className="text-gray-700">{batch.total_contacts}</strong></span>
            <span className="text-green-600">Found: <strong>{batch.found_count}</strong></span>
          </div>
          {batch.credits_used > 0 && (
            <span className="text-sm font-medium text-purple-600">
              -{batch.credits_used}
            </span>
          )}
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
            batch.status === 'completed' || batch.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
            batch.status === 'processing' || batch.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800' :
            'bg-gray-100 text-gray-600'
          }`}>
            {batch.status === 'COMPLETED' || batch.status === 'completed' ? 'Done' :
             batch.status === 'IN_PROGRESS' || batch.status === 'processing' ? 'Processing' :
             batch.status}
          </span>
          <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    );

    const renderJobRow = (job: EnrichmentJob, key: string) => (
      <div
        key={key}
        onClick={() => fetchJobDetails(job)}
        className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
      >
        <div className="flex items-center gap-3">
          {renderCategoryIcon('stack', 'orange')}
          <div>
            <p className="text-sm font-medium text-gray-900">
              Bulk Enrichment - {job.total_rows} contacts
            </p>
            <p className="text-xs text-gray-500">
              by {job.user_name || job.user_email}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-500">Sent: <strong className="text-gray-700">{job.total_rows}</strong></span>
            <span className="text-green-600">Found: <strong>{job.successful_rows}</strong></span>
          </div>
          <span className="text-sm font-medium text-purple-600">
            -{job.credits_used}
          </span>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
            job.status === 'completed' ? 'bg-green-100 text-green-800' :
            job.status === 'processing' ? 'bg-blue-100 text-blue-800' :
            'bg-red-100 text-red-800'
          }`}>
            {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
          </span>
          <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    );

    return (
      <div className="space-y-4">
        {Object.entries(grouped).map(([dateLabel, items]) => (
          <div key={dateLabel}>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">{dateLabel}</p>
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden divide-y divide-gray-100">
              {items.map(item => {
                if (item.type === 'fullenrich_batch' && item.batch) {
                  return renderBatchRow(item.batch, item.key);
                }

                if (item.type === 'auto_topup' && item.topup) {
                  const t = item.topup;
                  const ok = t.status === 'paid' || t.status === 'paid_unfulfilled';
                  const price = `£${(t.price_pence / 100).toLocaleString('en-GB', { minimumFractionDigits: t.price_pence % 100 ? 2 : 0, maximumFractionDigits: 2 })}`;
                  return (
                    <div key={item.key} className={`flex items-center justify-between px-4 py-3 ${ok ? 'bg-green-50/40' : 'bg-red-50/40'}`}>
                      <div className="flex items-center gap-3">
                        {renderCategoryIcon('bolt', ok ? 'green' : 'gray')}
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {ok ? 'Automatic top-up' : 'Automatic top-up failed'}
                          </p>
                          <p className="text-xs text-gray-500 max-w-md truncate">
                            {ok
                              ? <>{t.credits.toLocaleString()} credits for {price} on {t.billed_to}'s card
                                  {t.threshold != null && <> · balance fell below {t.threshold.toLocaleString()}</>}
                                  {t.hosted_invoice_url && <> · <a href={t.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-purple-600 hover:underline">invoice</a></>}</>
                              : <>{t.error || 'The card could not be charged.'} Nothing was charged; automatic top-up was switched off.</>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-xs text-gray-500">{t.triggered_by || t.billed_to}</span>
                        <span className={`text-sm font-medium ${ok ? 'text-green-600' : 'text-red-600'}`}>
                          {ok ? `+${t.credits.toLocaleString()}` : '—'}
                        </span>
                        <span className="text-xs text-gray-400 w-16 text-right">
                          {new Date(t.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  );
                }

                if (item.type === 'bulk_job' && item.job) {
                  return renderJobRow(item.job, item.key);
                }

                if (item.type === 'transaction' && item.transaction) {
                  const tx = item.transaction;
                  const cat = categoriseTransaction(tx.description);
                  return (
                    <div key={item.key} className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-3">
                        {renderCategoryIcon(cat.icon, cat.color)}
                        <div>
                          <p className="text-sm font-medium text-gray-900">{cat.label}</p>
                          <p className="text-xs text-gray-500 max-w-md truncate">{tx.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-xs text-gray-500">
                          {tx.user_name}
                        </span>
                        <span className="text-sm font-medium text-purple-600">
                          {tx.amount}
                        </span>
                        <span className="text-xs text-gray-400 w-16 text-right">
                          {new Date(tx.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  );
                }

                return null;
              })}
            </div>
          </div>
        ))}
        {timeline.length === 0 && (
          <div className="bg-white rounded-lg border border-gray-200 px-6 py-12 text-center text-gray-500">
            No usage history found
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // Job Detail View (drill-down into a bulk action)
  // ============================================
  const renderJobDetail = () => {
    if (!selectedJob) return null;

    return (
      <div>
        <button
          onClick={() => { setSelectedJob(null); setJobResults([]); }}
          className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to all actions
        </button>

        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Bulk Enrichment - {selectedJob.total_rows} contacts
            </h3>
            <p className="text-sm text-gray-500">
              by {selectedJob.user_name || selectedJob.user_email} &middot; {formatDate(selectedJob.uploaded_at)}
            </p>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-gray-600">Sent: <strong>{selectedJob.total_rows}</strong></span>
            <span className="text-green-600">Found: <strong>{selectedJob.successful_rows}</strong></span>
            <span className="text-red-600">Failed: <strong>{selectedJob.failed_rows}</strong></span>
            <span className="text-purple-600">Credits: <strong>{selectedJob.credits_used}</strong></span>
          </div>
        </div>

        {jobResultsLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">LinkedIn</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {jobResults.map(result => (
                  <tr key={result.id} className="hover:bg-gray-50">
                    <td className="px-6 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {result.first_name} {result.last_name}
                        </p>
                        {result.headline && (
                          <p className="text-xs text-gray-500 truncate max-w-xs">{result.headline}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-600">
                      {result.company_name || '-'}
                    </td>
                    <td className="px-6 py-3">
                      {result.linkedin_url ? (
                        <a
                          href={result.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline truncate block max-w-xs"
                        >
                          {result.linkedin_url.replace('https://www.linkedin.com/in/', '').replace(/\/$/, '')}
                        </a>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-right">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        result.enrichment_status === 'success'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {result.enrichment_status === 'success' ? (
                          <>
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Found
                          </>
                        ) : (
                          <>
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Failed
                          </>
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
                {jobResults.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                      No results found for this job
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // Credit Bundles Tab (like FullEnrich)
  // ============================================
  const renderBundlesTab = () => {
    return (
      <>
      {!IS_RECMAPPER && <AutoTopupPanel onChanged={fetchData} />}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="divide-y divide-gray-200">
          {bundles.map(bundle => {
            const usedCredits = bundle.credits_purchased - bundle.credits_remaining;
            const usagePct = bundle.credits_purchased > 0
              ? (usedCredits / bundle.credits_purchased) * 100
              : 0;
            const isExpired = bundle.status === 'expired';
            const isFullyUsed = bundle.credits_remaining === 0;

            return (
              <div key={bundle.id} className={`px-6 py-4 ${isExpired ? 'opacity-50' : ''}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                      isExpired ? 'bg-gray-100' : 'bg-purple-100'
                    }`}>
                      <svg className={`w-4 h-4 ${isExpired ? 'text-gray-400' : 'text-purple-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {bundle.source === 'auto_topup' ? 'Automatic top-up'
                          : bundle.source === 'tier_inclusion' ? 'Included with your plan'
                          : bundle.price_paid > 0 ? 'One-Time Payment' : 'Offered'}
                      </p>
                      {bundle.purchased_by && (
                        <p className="text-xs text-gray-500">by {bundle.purchased_by}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-8">
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Purchased</p>
                      <p className="text-sm text-gray-700">{formatDate(bundle.purchased_at)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Expires</p>
                      <p className={`text-sm ${isExpired ? 'text-red-600 font-medium' : 'text-gray-700'}`}>
                        {formatDate(bundle.expires_at)}
                      </p>
                    </div>
                    {isExpired && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Expired
                      </span>
                    )}
                    {isFullyUsed && !isExpired && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                        Used
                      </span>
                    )}
                  </div>
                </div>
                {/* Progress bar */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
                    <div
                      className={`h-full rounded-full transition-all ${
                        isExpired ? 'bg-gray-300' : 'bg-purple-200'
                      }`}
                      style={{ width: `${Math.min(usagePct, 100)}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-700 whitespace-nowrap min-w-[100px] text-right">
                    {usedCredits.toLocaleString()} / {bundle.credits_purchased.toLocaleString()}
                  </span>
                </div>
              </div>
            );
          })}
          {bundles.length === 0 && (
            <div className="px-6 py-12 text-center text-gray-500">
              No credit purchases found
            </div>
          )}
        </div>
      </div>
      </>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
      </div>
    );
  }

  return (
    <div>
      {/* Usage Chart */}
      {renderChart()}

      {/* Sub-tabs: Members | Credit Bundles | Usage Log */}
      <div className="flex items-center gap-6 mb-4">
        <button
          onClick={() => { setActiveSubTab('members'); setSelectedJob(null); setSelectedBatch(null); }}
          className={`flex items-center gap-2 pb-2 text-sm font-medium border-b-2 transition-colors ${
            activeSubTab === 'members'
              ? 'border-purple-500 text-purple-700'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Members
        </button>
        <button
          onClick={() => { setActiveSubTab('bundles'); setSelectedJob(null); setSelectedBatch(null); }}
          className={`flex items-center gap-2 pb-2 text-sm font-medium border-b-2 transition-colors ${
            activeSubTab === 'bundles'
              ? 'border-purple-500 text-purple-700'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z" />
          </svg>
          Credit Bundles
        </button>
        <button
          onClick={() => { setActiveSubTab('usage'); setSelectedJob(null); setSelectedBatch(null); }}
          className={`flex items-center gap-2 pb-2 text-sm font-medium border-b-2 transition-colors ${
            activeSubTab === 'usage'
              ? 'border-purple-500 text-purple-700'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
          Usage Log
        </button>
      </div>

      {activeSubTab === 'members' && renderMembersTab()}
      {activeSubTab === 'bundles' && renderBundlesTab()}
      {activeSubTab === 'usage' && renderUsageTab()}
    </div>
  );
};

// ============================================
// MemberRow Component (for credit limit toggle)
// ============================================
const MemberRow: React.FC<{
  member: TeamMember;
  isAdmin: boolean;
  saving: boolean;
  onUpdateLimit: (memberId: number, enabled: boolean, limit: number) => void;
  monthlySummary: MonthlySummary[];
}> = ({ member, isAdmin, saving, onUpdateLimit, monthlySummary }) => {
  const [limitEnabled, setLimitEnabled] = useState(member.credit_limit_enabled);
  const [limitValue, setLimitValue] = useState(member.credit_limit || 0);
  const [editing, setEditing] = useState(false);

  const handleToggle = () => {
    const newEnabled = !limitEnabled;
    setLimitEnabled(newEnabled);
    if (!newEnabled) {
      onUpdateLimit(member.id, false, 0);
    } else {
      setEditing(true);
    }
  };

  const handleSaveLimit = () => {
    onUpdateLimit(member.id, true, limitValue);
    setEditing(false);
  };

  const roleLabel = member.team_role
    ? member.team_role.charAt(0).toUpperCase() + member.team_role.slice(1)
    : 'Owner';

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4">
        <div>
          <p className="text-sm font-medium text-gray-900">
            {member.first_name} {member.last_name}
          </p>
          <p className="text-xs text-gray-500">{member.email}</p>
        </div>
      </td>
      <td className="px-6 py-4 text-sm text-gray-600">
        {roleLabel}
      </td>
      <td className="px-6 py-4 text-right">
        <div className="relative inline-block group/usage">
          <span className="inline-flex items-center gap-1 text-sm text-gray-700 cursor-default">
            <svg className="w-4 h-4 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            {parseInt(String(member.usage_this_cycle)).toLocaleString()}
          </span>
          {/* 3-month summary popup - opens above */}
          {monthlySummary.length > 0 && (
            <div className="absolute right-0 bottom-full mb-2 z-50 bg-gray-900 text-white rounded-lg shadow-xl p-4 opacity-0 group-hover/usage:opacity-100 transition-opacity pointer-events-none min-w-[260px] text-left">
              {monthlySummary.map((m, idx) => (
                <div key={m.month} className={idx > 0 ? 'mt-3 pt-3 border-t border-gray-700' : ''}>
                  <p className="text-sm font-medium mb-2 flex items-center gap-2">
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {m.month}
                  </p>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="flex items-center gap-1 bg-gray-800 rounded px-2 py-1">
                      <span className="w-2 h-2 rounded-full bg-green-400" />
                      {m.total.toLocaleString()}
                    </span>
                    <svg className="w-3 h-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                    {m.phone > 0 && (
                      <span className="flex items-center gap-1 text-yellow-400" title="Phone">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        {m.phone.toLocaleString()}
                      </span>
                    )}
                    {m.work_email > 0 && (
                      <span className="flex items-center gap-1 text-blue-400" title="Work Email">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {m.work_email.toLocaleString()}
                      </span>
                    )}
                    {m.personal_email > 0 && (
                      <span className="flex items-center gap-1 text-green-400" title="Personal Email">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {m.personal_email.toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </td>
      <td className="px-6 py-4 text-right">
        {isAdmin ? (
          <div className="flex items-center justify-end gap-2">
            {editing ? (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={limitValue}
                  onChange={(e) => setLimitValue(parseInt(e.target.value) || 0)}
                  className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
                  placeholder="Limit"
                  min="0"
                  step="100"
                />
                <button
                  onClick={handleSaveLimit}
                  disabled={saving}
                  className="px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
                >
                  Save
                </button>
                <button
                  onClick={() => { setEditing(false); setLimitEnabled(member.credit_limit_enabled); }}
                  className="px-2 py-1 text-xs text-gray-600 hover:text-gray-900"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <>
                {limitEnabled && (
                  <button
                    onClick={() => setEditing(true)}
                    className="text-sm text-gray-700 hover:text-purple-600 mr-1"
                  >
                    {(member.credit_limit || 0).toLocaleString()}
                  </button>
                )}
                <button
                  onClick={handleToggle}
                  disabled={saving}
                  className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    limitEnabled ? 'bg-purple-600' : 'bg-gray-200'
                  } ${saving ? 'opacity-50' : ''}`}
                >
                  <span
                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      limitEnabled ? 'translate-x-4' : 'translate-x-0'
                    }`}
                  />
                </button>
              </>
            )}
          </div>
        ) : (
          <span className="text-sm text-gray-500">
            {member.credit_limit_enabled ? `${(member.credit_limit || 0).toLocaleString()}/cycle` : '-'}
          </span>
        )}
      </td>
    </tr>
  );
};

export default CreditUsage;
